import { ComponentFactoryResolver, ComponentRef, Injectable, ViewContainerRef } from "@angular/core";
import { CellHoverTarget } from "./popover.models";
import { HtmlElementAnimationService } from "@nms-ng2/app/shared/services/dom/html-element-animation.service";
import { NmsDiagramCardComponent } from "@nms-angular-toolkit/nms-diagram-card";

/**
 * Serviço responsável por construir o popover.
 */
@Injectable({
    providedIn: "root"
})
export class PopoverBuilderService {

    constructor(private readonly resolver: ComponentFactoryResolver,
            private readonly htmlElementAnimationService: HtmlElementAnimationService) {
    }

    /**
     * Método responsável por construir e inserir o popover na tela.
     *
     * @param viewContainerRef - Referência do container onde o popover será inserido.
     * @param target - Célula do gráfico que recebeu um evento de hover.
     *
     * @returns - o popover criado.
     */
    build(viewContainerRef: ViewContainerRef, target: CellHoverTarget): ComponentRef<NmsDiagramCardComponent> {
        const { component, popover } = this.instantiatePopover(viewContainerRef);

        component.instance.data = target.data;

        this.setPosition(target, popover);
        this.htmlElementAnimationService.opacityIn(popover);

        return component;
    }

    /**
     * Calcula a posição do popover evitando que ultrapasse os limites da janela.
     *
     * Caso ele fosse ultrapassar a tela na direita, ele será instanciado o máximo para direita possível
     * para que ele ainda possa ser exibido no seu tamanho normal, aleḿ de uma pequena margem.
     *
     * @param rect - Posição da célula a qual o popover pertence.
     * @param popover - Estrutura HTML do popover
     * @param cell - Célula a qual o popover pertence.
     *
     * @returns - posição do popover em relação a célula e a janela como um todo.
     */
    private getElementLeftPosition(rect: DOMRect, popover: HTMLElement): number {
        const isOutOfArea = window.innerWidth < (rect.right + popover.offsetWidth);

        if (isOutOfArea) {
            return window.innerWidth - popover.offsetWidth - 10;
        }

        return rect.right + 5;
    }

    /**
     * Cria uma instância do componente {@see NmsDiagramCardComponent} para o popover.
     *
     * @param viewContainerRef Referência do container onde o popover será inserido.
     *
     * @returns Um objeto com o popover como {@see ComponentRef} do Angular e também como elemento HTML ({@see HTMLElement}).
     */
    private instantiatePopover(viewContainerRef: ViewContainerRef) {
        const componentFactory = this.resolver.resolveComponentFactory(NmsDiagramCardComponent);
        const component = viewContainerRef.createComponent(componentFactory);
        const popover = component.location.nativeElement as HTMLElement;

        // Sem o position absolute, o recém instanciado elemento ocupa um espaço que por consequência altera o tamanho
        // de outros elementos na tela (e.g. Modal de Diagnóstico). Ou seja, no momento que for avaliar
        // o posicionamento da célula que recebeu o hover, aquela célula estava numa determinada posição
        // mas ao definir o position absolute do popover, a célula se ajustou para uma nova posição.
        // Por isso é necessário definir position como `absolute` antes de fazer os cálculos relacionados
        // a célula que recebeu o hover para obter um posicionamento consistente, sem efeitos colaterais.
        popover.style.position = "absolute";

        return { component, popover };
    }

    /**
     * Define a posição do popover.
     *
     * @param target Célula do gráfico que recebeu um evento de hover.
     * @param popover Estrutura HTML do popover
     */
    private setPosition(target: CellHoverTarget, popover: HTMLElement): void {
        // É necessário fazer dentro de um setTimeout pois precisa avançar o EventLoop para que o
        // componente já possua os dados e o tamanho final. Sem isso, o componente ainda teria um
        // tamanho provisório por não ter os dados renderizados.
        setTimeout(() => {
            const rect = target.getAnchorRect(target);
            const left = this.getElementLeftPosition(rect, popover);

            popover.style.top = `${rect.top}px`;
            popover.style.left = `${left}px`;
            popover.style.zIndex = "1000";
        });
    }
}
